import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
} from 'react';

import {
  useSession,
  type ModuleManifest,
  type RouteDefinition,
} from '@clientma/core';
import { Button, SkeletonRows } from '@clientma/ui';

import { AccessDenied, AppShell } from './AppShell';
import { ErrorBoundary } from './ErrorBoundary';
import { LoginPage } from './LoginPage';
import { ResetPasswordPage } from './ResetPasswordPage';
import { AcceptInvitationPage, VerifyEmailPage } from './TokenLandingPages';
import { PLATFORM_ROUTE_PREFIX, platformRoutes } from './platform-routes';

/**
 * A small history-based router.
 *
 * A routing library would bring its own data-loading model, its own opinions about layouts and
 * a migration every major version — for what this app needs, which is: match a path, lazily
 * load a product's screens, and check a permission before rendering. That fits in this file.
 */

interface ResolvedRoute {
  definition: RouteDefinition;
  params: Record<string, string>;
  Component: ComponentType;
}

export function ShellRouter({
  manifests,
  productName,
}: {
  manifests: ModuleManifest[];
  /**
   * What this deployment is. The suite workspace is ClientMa; a product running standalone is
   * that product, and a sign-in page that names the suite instead leaves the person unsure
   * what they are signing in to.
   */
  productName?: string;
}) {
  const { status, can } = useSession();

  const [location, setLocation] = useState(readLocation);
  const [routes, setRoutes] = useState<Map<string, RouteDefinition[]>>(new Map());

  const { path, search } = location;

  const navigate = useCallback((next: string) => {
    // An external or absolute URL leaves the SPA rather than being pushed onto its history.
    if (next.startsWith('http')) {
      window.location.href = next;
      return;
    }

    window.history.pushState({}, '', next);
    // Read back from the browser rather than storing `next` directly: a target may carry a query
    // string, and matching routes against a path with "?focus=..." still on the end finds nothing.
    setLocation(readLocation());
    window.scrollTo(0, 0);
  }, []);

  // Back and forward must work, so the browser's own navigation drives state too.
  useEffect(() => {
    const onPop = () => setLocation(readLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const activeManifest = useMemo(
    () => manifests.find((manifest) => path.startsWith(manifest.routePrefix)),
    [manifests, path],
  );

  // The workspace owns everything under /settings — people, roles and tenant configuration —
  // so it is resolved alongside the products rather than inside one of them.
  const isPlatformPath = path.startsWith(PLATFORM_ROUTE_PREFIX);

  // A product's routes load only when the user first enters it, so the shell bundle stays
  // small no matter how many products are registered.
  useEffect(() => {
    if (!activeManifest || routes.has(activeManifest.applicationKey)) return;

    let cancelled = false;

    void activeManifest.routes().then((module) => {
      if (cancelled) return;

      setRoutes((current) => {
        const next = new Map(current);
        next.set(activeManifest.applicationKey, module.default);
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [activeManifest, routes]);

  if (status === 'loading') {
    return (
      <div style={{ padding: 'var(--space-8)' }}>
        <SkeletonRows rows={4} />
      </div>
    );
  }

  // Reachable signed out on purpose: the token in the link is the credential, and the people
  // following these links are by definition not signed in yet.
  if (path.startsWith('/reset-password')) {
    return <ResetPasswordPage onDone={() => navigate('/')} />;
  }

  if (path.startsWith('/verify-email')) {
    return <VerifyEmailPage onDone={() => navigate('/')} />;
  }

  if (path.startsWith('/accept-invitation')) {
    return <AcceptInvitationPage onDone={() => navigate('/')} />;
  }

  if (status === 'signed-out') {
    // A single-product deployment names itself; the suite falls back to ClientMa.
    return <LoginPage productName={productName ?? soleProductName(manifests)} />;
  }

  const productRoutes = activeManifest
    ? (routes.get(activeManifest.applicationKey) ?? null)
    : null;

  return (
    <RouteSearchProvider value={search}>
      <ManifestsProvider value={manifests}>
      <AppShell manifests={manifests} path={path} onNavigate={navigate}>
        {/* Keyed on the path so navigating away from a broken screen clears the error. */}
        <ErrorBoundary resetKey={path}>
          <RouteOutlet
            path={path}
            routes={isPlatformPath ? platformRoutes : productRoutes}
            manifests={manifests}
            onNavigate={navigate}
            can={can}
            isLoadingRoutes={
              !isPlatformPath && activeManifest !== undefined && productRoutes === null
            }
          />
        </ErrorBoundary>
      </AppShell>
      </ManifestsProvider>
    </RouteSearchProvider>
  );
}

/** The current path and query, read together so the two can never disagree. */
function readLocation(): { path: string; search: string } {
  return { path: window.location.pathname, search: window.location.search };
}

/**
 * The first navigation entry the user holds the permission for, across all products.
 *
 * Falls back to a product's own root only when nothing else matches, so a user with an unusual
 * role still lands somewhere rather than nowhere.
 */
function firstPermittedPath(
  manifests: ModuleManifest[],
  can: (permission: string) => boolean,
): string | undefined {
  for (const manifest of manifests) {
    const entry = manifest.nav.find((item) => !item.requiresPermission || can(item.requiresPermission));
    if (entry) return entry.path;
  }

  return manifests[0]?.routePrefix;
}

/** The product's name when a deployment hosts exactly one, so the sign-in page can say it. */
function soleProductName(manifests: ModuleManifest[]): string | undefined {
  return manifests.length === 1 ? manifests[0]?.displayName : undefined;
}

function RouteOutlet({
  path,
  routes,
  manifests,
  onNavigate,
  can,
  isLoadingRoutes,
}: {
  path: string;
  routes: RouteDefinition[] | null;
  manifests: ModuleManifest[];
  onNavigate: (path: string) => void;
  can: (permission: string) => boolean;
  isLoadingRoutes: boolean;
}) {
  const resolved = useMemo<ResolvedRoute | null>(() => {
    if (!routes) return null;

    for (const definition of routes) {
      const params = matchPath(definition.path, path);
      if (!params) continue;

      return {
        definition,
        params,
        Component: lazy(definition.element),
      };
    }

    return null;
  }, [routes, path]);

  if (isLoadingRoutes) {
    return (
      <div className="cma-page">
        <SkeletonRows rows={5} />
      </div>
    );
  }

  // Landing at the root: the first screen the user can actually open. Taking the first nav
  // entry regardless of permission means someone whose role does not cover it — a marketing
  // user against the sales dashboard — meets a permission error as their first impression.
  if (path === '/' || path === '') {
    const destination = firstPermittedPath(manifests, can);

    if (destination) {
      // Deferred to a microtask so the redirect is not a state update during render.
      queueMicrotask(() => onNavigate(destination));
      return null;
    }
  }

  if (!resolved) {
    // Somewhere to go from here. People reach this page by following a link that has gone stale,
    // and leaving them on a dead end means using the browser's Back button to escape the app's
    // own navigation.
    const home = manifests[0]?.nav[0]?.path ?? manifests[0]?.routePrefix;

    return (
      <div className="cma-page">
        <div className="cma-card" style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
          <h2 className="cma-card__title">Page not found</h2>
          <p style={{ marginTop: 'var(--space-2)', color: 'var(--text-muted)' }}>
            <code>{path}</code> does not match anything in this workspace. The link may be out of
            date, or the record may have been removed.
          </p>

          {home && (
            <div style={{ marginTop: 'var(--space-5)' }}>
              <Button variant="primary" onClick={() => onNavigate(home)}>
                Go to {manifests[0]?.displayName ?? 'the workspace'}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (resolved.definition.requiresPermission && !can(resolved.definition.requiresPermission)) {
    return <AccessDenied permission={resolved.definition.requiresPermission} />;
  }

  const { Component, params } = resolved;

  return (
    <Suspense
      fallback={
        <div className="cma-page">
          <SkeletonRows rows={4} />
        </div>
      }
    >
      <RouteParamsProvider value={params}>
        {/*
          Keyed on the route it came from. Several screens serve more than one route — the
          supplier list is also the approvals list and the watchlist — and without a key React
          keeps one instance across all of them, so the filters each route sets at mount are
          never applied on the second visit. The pattern, not the path: two records of the same
          definition should not tear the screen down between them.
        */}
        <Component key={resolved.definition.path} />
      </RouteParamsProvider>
    </Suspense>
  );
}

/**
 * Matches a pattern like `/crm/accounts/:id` against a path.
 * Returns the captured params, or null when it does not match.
 */
function matchPath(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);

  // A trailing wildcard lets a screen own its own sub-navigation.
  const hasWildcard = patternParts[patternParts.length - 1] === '*';

  if (!hasWildcard && patternParts.length !== pathParts.length) return null;
  if (hasWildcard && pathParts.length < patternParts.length - 1) return null;

  const params: Record<string, string> = {};

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i]!;

    if (patternPart === '*') break;

    const pathPart = pathParts[i];
    if (pathPart === undefined) return null;

    if (patternPart.startsWith(':')) {
      params[patternPart.slice(1)] = decodeURIComponent(pathPart);
      continue;
    }

    if (patternPart !== pathPart) return null;
  }

  return params;
}

// ─────────────────────────────────────────────────────── route params context

import { createContext, useContext, type ReactNode } from 'react';

const RouteParamsContext = createContext<Record<string, string>>({});

/**
 * The installed products, readable from any screen.
 *
 * The settings landing page has to list what each product contributes to Administration, and
 * building that list twice — once for the rail and once for the page — is how the two come to
 * disagree about what exists.
 */
const ManifestsContext = createContext<ModuleManifest[]>([]);

function ManifestsProvider({ value, children }: { value: ModuleManifest[]; children: ReactNode }) {
  return <ManifestsContext.Provider value={value}>{children}</ManifestsContext.Provider>;
}

/** The products this session can see. */
export function useManifests(): ModuleManifest[] {
  return useContext(ManifestsContext);
}

function RouteParamsProvider({
  value,
  children,
}: {
  value: Record<string, string>;
  children: ReactNode;
}) {
  return <RouteParamsContext.Provider value={value}>{children}</RouteParamsContext.Provider>;
}

const RouteSearchContext = createContext<string>('');

function RouteSearchProvider({ value, children }: { value: string; children: ReactNode }) {
  return <RouteSearchContext.Provider value={value}>{children}</RouteSearchContext.Provider>;
}

/**
 * A query parameter for the current route, e.g. `focus` from `/crm/reminders?focus=...`.
 *
 * Read from router state rather than `window.location` directly, so a screen re-renders when the
 * query changes without the path changing.
 */
export function useRouteQuery(key: string): string | null {
  const search = useContext(RouteSearchContext);
  return useMemo(() => new URLSearchParams(search).get(key), [search, key]);
}

/** Path parameters for the current route, e.g. `:id` from `/crm/accounts/:id`. */
export function useRouteParams(): Record<string, string> {
  return useContext(RouteParamsContext);
}

/** Programmatic navigation from inside a screen. */
export function useNavigate(): (path: string) => void {
  return useCallback((path: string) => {
    if (path.startsWith('http')) {
      window.location.href = path;
      return;
    }

    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo(0, 0);
  }, []);
}
